import { useId } from 'react'

import { clsx } from 'clsx'
import { nanoid } from 'nanoid'
import { useFieldArray, UseFormReturn } from 'react-hook-form'

import { TrashIcon } from '@bettermode/icons/line/Trash'
import { Button, IconButton } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { Form } from '../../Form/index.js'
import { T, useI18n } from '../../i18n/index.js'

import { PollFormValues } from './types.js'

const MAX_OPTIONS = 10

interface PollOptionsProps {
  form: UseFormReturn<PollFormValues>
  disabled?: boolean
}

export function PollFormOptions({ form, disabled }: PollOptionsProps) {
  const id = useId()
  const { $t } = useI18n()
  const { fields, remove, append } = useFieldArray({
    control: form.control,
    name: 'options',
  })

  const optionsCount = `${fields.length} / ${MAX_OPTIONS}`

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <FormControl.Label htmlFor="poll-options">
          <T id="Poll.Form.Choices" defaultMessage="Choices" />
        </FormControl.Label>
        <span className="text-content-muted text-sm bg-background-muted rounded-md px-2 py-1">
          {optionsCount}
        </span>
      </div>

      <legend id={`poll-options-${id}`}>
        <div className="flex flex-col gap-2">
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <div className="flex-1">
                <Form.Input
                  id={`poll-option-${id}-${index}`}
                  name={`options.${index}.value`}
                  placeholder={$t(
                    {
                      id: 'Poll.Form.ChoicePlaceholder',
                      defaultMessage: 'Choice {index, number}',
                    },
                    { index: index + 1 },
                  )}
                  className="flex-1"
                  disabled={disabled}
                  leadingAddon={
                    <span className="text-content-muted pe-3">
                      <span>{getOptionNumber(index)}</span>
                    </span>
                  }
                  validation={{
                    required: true,
                  }}
                  trailingAddon={
                    <div
                      className={clsx(
                        'flex-1',
                        fields.length <= 2 && 'cursor-not-allowed',
                        disabled && 'hidden',
                      )}
                    >
                      <IconButton
                        icon={<TrashIcon />}
                        type="button"
                        size="sm"
                        className="p-0"
                        variant="tertiaryNeutral"
                        onClick={() => remove(index)}
                        disabled={fields.length <= 2}
                      />
                    </div>
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </legend>
      {!disabled && fields.length < MAX_OPTIONS && (
        <Button
          fullWidth
          variant="tertiaryNeutral"
          type="button"
          textAlign="start"
          onClick={() => append({ id: nanoid(5), value: '' })}
          leadingIcon={<SvgIcon name="plus" />}
        >
          <T id="Poll.Form.Choices.Add" defaultMessage="Add another choice" />
        </Button>
      )}
    </div>
  )
}

function getOptionNumber(index: number) {
  return `${index + 1}.`
}
